import { cn } from "@ui/lib";
import { createElement, type ReactNode } from "react";

/**
 * Text as WhatsApp shows it: its own markup (`*bold*`, `_italic_`, `~strike~`, `` `mono` `` and
 * ```` ``` ```` blocks), which is not Markdown, drawn as plain React elements so nothing in the
 * text can become HTML. A marker opens only before a non-space and after a non-letter, and
 * closes only after a non-space and before a non-letter, as WhatsApp does ("2*3*4" stays as it
 * is). A marker without its pair is left as text. Markup never spans lines, except a code block.
 */
export function WhatsAppText({
	text,
	className,
}: {
	text: string;
	className?: string;
}) {
	return (
		<span dir="auto" className={cn("whitespace-pre-wrap", className)}>
			{whatsAppNodes(text)}
		</span>
	);
}

const INLINE: Record<string, "strong" | "em" | "s" | "code"> = {
	"*": "strong",
	_: "em",
	"~": "s",
	"`": "code",
};

const WORD = /[\p{L}\p{N}]/u;
const SPACE = /\s/;
const BLOCK = /```([\s\S]+?)```/g;
const MONO = "rounded-md bg-muted px-1 font-mono text-[0.92em]";

/** The React nodes of a WhatsApp text, exported for tests. */
export function whatsAppNodes(text: string): ReactNode[] {
	const nodes: ReactNode[] = [];
	let last = 0;
	for (const match of text.matchAll(BLOCK)) {
		const start = match.index ?? 0;
		nodes.push(...inline(text.slice(last, start), `t${last}`));
		nodes.push(
			createElement(
				"code",
				{
					key: `b${start}`,
					className: cn(
						MONO,
						"block w-fit max-w-full overflow-x-auto py-1",
					),
				},
				match[1],
			),
		);
		last = start + match[0].length;
	}
	nodes.push(...inline(text.slice(last), `t${last}`));
	return nodes;
}

function inline(text: string, key: string): ReactNode[] {
	const nodes: ReactNode[] = [];
	let plain = "";
	let i = 0;
	while (i < text.length) {
		const mark = text[i] ?? "";
		const tag = INLINE[mark];
		const end = tag && opens(text, i) ? closing(text, i) : -1;
		if (!tag || end < 0) {
			plain += mark;
			i += 1;
			continue;
		}
		if (plain) {
			nodes.push(plain);
			plain = "";
		}
		const inner = text.slice(i + 1, end);
		const id = `${key}.${i}`;
		nodes.push(
			createElement(
				tag,
				{
					key: id,
					className:
						tag === "code"
							? MONO
							: tag === "strong"
								? "font-semibold"
								: undefined,
				},
				// Monospace shows its content as written; the others may nest.
				...(tag === "code" ? [inner] : inline(inner, id)),
			),
		);
		i = end + 1;
	}
	if (plain) {
		nodes.push(plain);
	}
	return nodes;
}

function opens(text: string, at: number): boolean {
	const next = text[at + 1];
	const previous = text[at - 1];
	return (
		next !== undefined &&
		next !== text[at] &&
		!SPACE.test(next) &&
		(previous === undefined || !WORD.test(previous))
	);
}

/** Where the marker opened at `at` closes on the same line, or -1. */
function closing(text: string, at: number): number {
	const mark = text[at];
	for (let j = at + 2; j < text.length; j += 1) {
		const char = text[j];
		if (char === "\n") {
			return -1;
		}
		const after = text[j + 1];
		if (
			char === mark &&
			!SPACE.test(text[j - 1] ?? " ") &&
			(after === undefined || !WORD.test(after))
		) {
			return j;
		}
	}
	return -1;
}
